<template>
  <section class="marketing-sms-form">
    <marketing-sms-form-content v-if="step === 1"
                                :initial-data="form"
                                :is-edit="isEdit"
                                :view="view"
                                @saveDraft="saveContentDraft"
                                @advance="setContent" />

    <app-form v-if="step === 2"
              ref="sendersForm"
              @submit="setSenders">
      <marketing-form-senders v-model="form.senders"
                              :channel-data="smsData"
                              :view="view"
                              channel-type="SMS"
                              @changed-external="hasExternalFileChanged = true"
                              @change-timeFilterActive = "isCustomSegmentedListTimeFilterActive = $event"
                              @has-edited="setHasEdited" />

      <div class="marketing-sms-form-actions">
        <button v-if="!view"
                type="button"
                class="btn btn-round marketing-sms-form-actions-save btn-stretch"
                @click="saveDraft">
            {{ $t('message.marketingGenerics.message.saveDraft') }}
        </button>
        <button type="submit"
                class="btn btn-round btn-primary btn-stretch">
            {{ $t('message.generics.message.advance') }}
        </button>
      </div>
    </app-form>

    <app-form v-if="!view && step === 3"
              ref="sendForm"
              @submit="setScheduleAndSave">
      <marketing-form-send v-if="!view && step === 3"
                           v-model="form.send"
                           :initial-data="form"
                           @testSend="openTestModal" />

      <div class="marketing-sms-form-actions">
        <button v-if="!view"
                type="button"
                class="btn btn-round marketing-sms-form-actions-save btn-stretch"
                @click="saveScheduleDraft">
            {{ $t('message.marketingGenerics.message.saveDraft') }}
        </button>
        <button type="submit"
                class="btn btn-round btn-primary btn-stretch">
            {{ $t('message.generics.message.advance') }}
        </button>
      </div>
    </app-form>

    <marketing-sms-test-modal v-model="showTestModal"
                              :sms-data="smsData"
                              @advance="hideTestModal" />
  </section>
</template>

<script>
import AppForm from "@components/form/Form.vue";
import MarketingSmsFormContent from "@views/marketing/components/MarketingSmsFormContent";
import MarketingFormSenders from "@components/modules/marketing/generic/form/senders/MarketingFormSenders";
import MarketingFormSend from "@components/modules/marketing/generic/form/send/MarketingFormSend";
import MarketingSmsTestModal from "@views/marketing/components/MarketingSmsTestModal";
import EventBus from "@packages/event-bus";

export default {
  components: {
    MarketingSmsFormContent,
    MarketingFormSenders,
    MarketingFormSend,
    MarketingSmsTestModal,
    AppForm,
  },

  data() {
    return {
      step: 1,
      form: {
        title: undefined,
        content: undefined,
        send: {
          scheduleType: "NOW",
          schedule: {
            date: undefined,
            hour: undefined,
          },
        },
        senders: {
          sendType: undefined,
          configuration: undefined,
          sourceNote: undefined,
          file: undefined,
        },
        isAutomationFlow: false,
      },
      smsData: undefined,
      showTestModal: false,
      hasEdited: false,
      hasExternalFileChanged: false,
      isCustomSegmentedListTimeFilterActive: false
    };
  },

  props: {
    item: {
      type: Object,
      required: false,
    },
    title: {
      type: String,
      required: false,
    },
    view: {
      type: Boolean,
      default: false,
    },
  },  

  methods: {
    setHasEdited() {
      this.hasEdited = true;
    },
    saveDraft() {
      if (this.form.senders.sendType) {
        const isExternalList = this.form.senders.sendType === "EXTERNAL";
        if(isExternalList && this.form.content.includes("{PRIMEIRO_NOME}")) {
          return EventBus.$emit("show-message", {
                  type: "ERROR",
                  message: "Não é possível utilizar base externa com variáveis no corpo da mensagem",
              });   
        } 
        this.setSenders(true);        
      } else {
        this.$router.push({ path: "/marketing/sms" });
        EventBus.$emit("show-message", {
          type: "SUCCESS",
          message: this.$t('message.successMessages.message.draftSaved'),
        });
      }
    },
    hideTestModal() {
      this.showTestModal = false;
    },
    openTestModal() {
      this.showTestModal = true;
    },
    setSmsEditData() {
      this.smsData = this.item;

      const scheduleArray = this.item.schedulerDate
        ? this.item.schedulerDate.split(" ")
        : [];

      this.form.title = this.item.title;
      this.form.content = this.item.content;
      this.form.send = {
        scheduleType: this.item.schedulerDate ? "SCHEDULE" : "NOW",
        schedule: {
          date: scheduleArray[0],
          hour: scheduleArray[1],
        },
      };

      this.form.senders = {
        sendType: this.item.sendType,
        configuration: this.item.configuration,
        sourceNote: this.item.sourceNote,
        file: undefined,
      };

      this.form.isAutomationFlow = this.item.isAutomationFlow;
    },
    saveContentDraft({ content, title }) {
      if(!content || content.length < 2) {
        return EventBus.$emit("show-message", {
          type: "ERROR",
          message: this.$t('message.errorMessages.message.smsContentAtLeatsTwoCharacters'),
      });
      }
      this.form.content = content;
      this.form.title = title;

      this.saveSms();

      EventBus.$emit("show-message", {
          type: "SUCCESS",
          message: this.$t('message.successMessages.message.draftSaved'),
      });
      this.$router.push({ path: "/marketing/sms" });
    },
    saveScheduleDraft() {
      this.saveSms();
      EventBus.$emit("show-message", {
          type: "SUCCESS",
          message: this.$t('message.successMessages.message.draftSaved'),
      });
      this.$router.push({ path: "/marketing/sms" });
    },
    advanceStep() {
      if (this.step < 3) {
        this.step += 1;
        EventBus.$emit("SET_SMS_FORM_STEP", this.step);

        if (this.step == 3) {
          this.showTestModal = true;
        }
      }
    },
    async setContent({ content, title }) {
      if (!this.view) {
        this.form.content = content;
        this.form.title = title;

        await this.saveSms(true);
      }

      this.advanceStep();
    },
    setScheduleAndSave() {
      this.sendSms();
    },
    async sendSms() {
      try {
        EventBus.$emit("SHOW_SPINNER");

        await this.$api.smsMessage.send({
          smsId: this.smsData.id,
          schedulerDate:
            this.form.send.scheduleType === "NOW"
              ? undefined
              : `${this.form.send.schedule.date} ${this.form.send.schedule.hour}:00`,
        });

        this.$router.push({ path: "/marketing/sms" });
      } catch (error) {
        //empty
      } finally {
        EventBus.$emit("CANCEL_SPINNER");
      }
    },
    async saveSms(advanceStep) {
      try {
        EventBus.$emit("SHOW_SPINNER");

        if (this.isEdit || this.hasSmsData) {
          await this.editSms(advanceStep);
        } else {
          await this.createSms();
        }
      } catch (error) {
        //empty
        console.log(error);
      } finally {
        EventBus.$emit("CANCEL_SPINNER");
      }
    },
    async createSms() {
      const sms = await this.$api.marketing.sms.message.create({
        ...this.form,
        scheduleType: this.form.send.scheduleType,
        schedulerDate:
          this.form.send.scheduleType === "NOW"
            ? undefined
            : `${this.form.send.schedule.date} ${this.form.send.schedule.hour}:00`,
      });

      this.smsData = sms;      
    },
    async editSms() {
      const sms = await this.$api.marketing.sms.message.edit({
        smsId: this.smsData.id,
        data: {
          scheduleType: this.form.send.scheduleType,
          schedulerDate:
            this.form.send.scheduleType === "NOW"
              ? undefined
              : `${this.form.send.schedule.date} ${this.form.send.schedule.hour}:00`,
          content: this.form.content,
          isAutomationFlow: this.form.isAutomationFlow,
          sendType: this.form.senders.sendType,
          sourceNote: this.form.senders.sourceNote,
          title: this.form.title,
          type: this.form.senders.sendType,
          file: this.form.senders.file,
          configuration: this.form.senders.configuration
        },
      });

      this.smsData = sms;
      
    },
    setStep(stepVal) {
      this.step = stepVal;
    },
    setSmsData({ smsData }) {
      this.smsData = smsData;
    },
    //FIX IT - SMS SENDER LOGIC
    setDataAndAdvance({ channelData, sourceNote }) {
      if (!this.view) {
        this.smsData = channelData;
        this.form.senders.sourceNote = sourceNote;
        this.form.senders.configuration = channelData.configuration
      }

      this.advanceStep();
    },
    async setSenders(isSaveDraft) {
      const isValid = await this.validateRecipientType();

      if(isSaveDraft) {
        if(!this.hasEdited) {
          this.$router.push({ path: "/marketing/sms" });
          EventBus.$emit("show-message", {
            type: "SUCCESS",
            message: this.$t('message.successMessages.message.draftSaved'),
          });
          return
        } else {
          if(isValid) {
            await this.setType();
            this.$router.push({ path: "/marketing/sms" });
            EventBus.$emit("show-message", {
              type: "SUCCESS",
              message: this.$t('message.successMessages.message.draftSaved'),
            });
          }
        }
      } else {
        if (this.view || !this.hasEdited) {
         return this.advanceStep();
        }

        if(isValid) {
          const isExternalList = this.form.senders.sendType === "EXTERNAL";
          if(isExternalList && this.form.content.includes("{PRIMEIRO_NOME}")) {
            return EventBus.$emit("show-message", {
            type: "ERROR",
            message: "Não é possível utilizar base externa com variáveis no corpo da mensagem",
            });        
          }

          try {
            let newchannelData = await this.setType();
  
            if (newchannelData) {
              this.setDataAndAdvance({
                channelData: newchannelData,
                sourceNote: newchannelData.sourceNote,
              });
            } else {
              this.advanceStep();
            }            
          } catch (error) {
            if(isExternalList) {
              this.form.senders.file = undefined
            } 
            return EventBus.$emit("show-message", {
              type: "ERROR",
              message: error,
            })
          }          
        }
      }
      
    },
    validateRecipientType() {
      const isExternalList = this.form.senders.sendType === "EXTERNAL";
      const isSegmentedList =
        this.form.senders.sendType === "SIMPLE_SEGMENTED_LIST";
      const isCustomSegmentedList = this.sendType === "CUSTOM_SEGMENTED_LIST"  

      if (
        isExternalList &&
        !this.form.senders.file &&
        !Array.isArray(this.form.senders.configuration)
      ) {
        EventBus.$emit("show-message", {
          type: "ERROR",
          message: this.$t('message.errorMessages.message.selectFile'),
        });

        return false
      }

      else if (
        isSegmentedList &&
        (!this.form.senders.configuration ||
          Array.isArray(this.form.senders.configuration))
      ) {        
        EventBus.$emit("show-message", {
          type: "ERROR",
          message: this.$t('message.errorMessages.message.selectList'),
        });

        return false
      }

      else if(isCustomSegmentedList && this.isCustomSegmentedListTimeFilterActive && !this.form.senders.configuration.timeFilterType) {
        EventBus.$emit("show-message", {
          type: "ERROR",
          message: this.$t('message.errorMessages.message.selectEntryCriteria'),
        });

        return false
      }

      else {
        return true
      }
    },
    async setType() {
      this.validateRecipientType();

      const isExternalList = this.form.senders.sendType === "EXTERNAL";

      if (isExternalList && !this.hasExternalFileChanged) {
        return;
      }      

      try {
        this.checkValidationsForCustomSegmentedList()        

        EventBus.$emit("SHOW_SPINNER");

        const channelData = await this.$api.marketing.sms.message.setSendType({
          type: this.form.senders.sendType,
          smsId: this.smsData.id,
          file: this.form.senders.file,
          sourceNote: this.form.senders.sourceNote,
          configurationSimplesSegmentedList: this.form.senders.configuration,
        });

        return channelData;
      } catch (error) {
        console.log(error);
        throw error.response.data.message
        //empty
      } finally {
        EventBus.$emit("CANCEL_SPINNER");
      }
    },
    checkValidationsForCustomSegmentedList() {
      if (this.form.senders.configuration === undefined) {
        this.form.senders.configuration = {
          timeFilterType: undefined,
          gender:  null,
          startAge:  0,
          endAge: 100,
          timeFilter: {
              type:  "PERIOD",
              startPeriod:  new Date().toISOString().substring(0, 10),
              endPeriod:  new Date().toISOString().substring(0, 10),
              days:  undefined   
          }
        }
      }
    }

  },

  created() {
    if (this.item) {      
      this.setSmsEditData();
    } else {
      this.form.title = this.title;
    }

    EventBus.$on("SET_SMS_FORM_STEP", this.setStep);
    EventBus.$on("external-has-not-changed", () => this.hasExternalFileChanged = false);
  },

  beforeDestroy() {
    EventBus.$off("SET_SMS_FORM_STEP", this.setStep);
    EventBus.$off("external-has-not-changed", () => this.hasExternalFileChanged = false);
  },

  computed: {
    isEdit() {
      return !!this.item;
    },
    hasSmsData() {
      return !!this.smsData;
    },
    sendType() {
      return this.form.senders.sendType
    }    
  },

  mixins: [],
};
</script>

<style lang="scss" scoped>
.marketing-sms-form {
  width: 60%;
  margin: 50px auto 0;
  padding-bottom: 50px;

  .marketing-sms-form-actions {
    display: flex;
    justify-content: center;

    .marketing-sms-form-actions-save {
      border: 1px solid gray;
      margin-right: 50px;
    }
  }
}
</style>
